import { IGNORED_ATTRIBUTES, ECOMMERCE_EVENTS, PRODUCT_ATTRIBUTION_EVENTS } from "../../config.js";
import { collectComponentAttributes } from "./component-attributes.js";
import { pushDlEventPayload } from "./dl-event-push.js";
import { deferDlEvent, SUCCESS_SIGNAL_REQUIRED_ATTRIBUTE } from "./deferred-dl-event.js";

// Events, bei denen item_list_id/item_list_name laut GA4-Spec auf Event-Ebene gehören.
// Für alle anderen Ecommerce-Events (z.B. add_to_cart) wird die Listen-Herkunft pro Item
// aus der productAttribution (localStorage) gezogen — das hier abgeleitete component-Attribut
// (z.B. "hinzugefuegt-overlay") würde sonst die echte Attribution überschreiben.
const EVENT_LEVEL_LIST_PARAMS_EVENTS = ["select_item", "view_item_list"];

/**
 * Behandelt dl-event Attribute für beliebige DOM Events (click, copy, etc.)
 * Unterstützt alle interaktiven Events (add_to_cart, add_to_wishlist, etc.)
 *
 * Trägt das Element zusätzlich success-signal-required, wird das Event nicht sofort gesendet, sondern
 * bis zur Erfolgsbestätigung durch die Frontend-Komponente zurückgehalten.
 *
 * @param {Event} event - DOM Event (click, copy, etc.)
 * @param {Object} baseParams - Optionale zusätzliche Parameter (z.B. {content: "..."} für copy)
 * @returns {boolean} true wenn Event behandelt wurde, false sonst
 */
export function handleDlEvent(event, baseParams = {}) {
	const payload = buildDlEventPayload(event, baseParams);
	if (!payload) {
		return false;
	}

	if (payload.element.hasAttribute(SUCCESS_SIGNAL_REQUIRED_ATTRIBUTE)) {
		return deferDlEvent(payload.element, payload);
	}

	return pushDlEventPayload(payload);
}

/**
 * Baut den DataLayer-Payload für ein dl-event zum Auslösezeitpunkt auf, ohne ihn zu senden.
 *
 * Der Payload MUSS beim Auslösen gebaut werden: Bis zu einer eventuellen Erfolgsbestätigung
 * kann sich der DOM-Kontext (component, impression, product-list, dl-product Kinder) verändert
 * haben, und baseParams wie der kopierte Text sind später nicht mehr rekonstruierbar.
 *
 * @param {Event} event - DOM Event (click, copy, etc.)
 * @param {Object} baseParams - Optionale zusätzliche Parameter (z.B. {content: "..."} für copy)
 * @returns {{element: HTMLElement, eventName: string, params: Object, items?: Array<Object>}|null}
 */
export function buildDlEventPayload(event, baseParams = {}) {
	const { target } = event;

	// Finde Element mit dl-event Attribut
	const dlEventElement = target.hasAttribute("dl-event")
		? target
		: target.closest("[dl-event]");

	if (!dlEventElement) {
		return null;
	}

	// Lese Event-Namen
	const eventName = dlEventElement.getAttribute("dl-event");
	if (!eventName) {
		return null;
	}

	// Sammle alle Attribute vom dl-event Element
	const attributes = collectDlEventAttributes(dlEventElement);

	// Merge baseParams mit attributes (baseParams haben Vorrang)
	const mergedAttributes = { ...attributes, ...baseParams };

	// Prüfe ob es ein Ecommerce Event ist
	const isEcommerce = isEcommerceEvent(eventName, dlEventElement);

	if (isEcommerce) {
		// Ecommerce Event: mit Items-Array
		return buildDlEcommercePayload(eventName, dlEventElement, mergedAttributes);
	}

	// Non-Ecommerce Event: analog zu click Event
	return buildDlNonEcommercePayload(eventName, dlEventElement, mergedAttributes);
}

/**
 * Prüft ob Element oder Parent ein dl-event Attribut hat
 * @param {HTMLElement} element - Element zum Prüfen
 * @returns {boolean}
 */
export function hasDlEventAttribute(element) {
	return !!(element.hasAttribute("dl-event") || element.closest("[dl-event]"));
}

/**
 * Prüft ob ein Event ein Ecommerce Event ist
 * Bedingung: Event-Name in ECOMMERCE_EVENTS UND (dl-product Kinder ODER product-id vorhanden)
 * @private
 */
function isEcommerceEvent(eventName, element) {
	// Ist es ein bekanntes Ecommerce Event?
	if (!ECOMMERCE_EVENTS.includes(eventName)) {
		return false;
	}

	// Hat es dl-product Kinder oder product-id?
	const hasDlProduct = !!element.querySelector("dl-product");
	const hasProductId = !!(
		element.hasAttribute("product-id") ||
		element.querySelector("[product-id]")
	);

	return hasDlProduct || hasProductId;
}

/**
 * Baut den Payload für ein Ecommerce dl-event
 * @private
 */
function buildDlEcommercePayload(eventName, element, attributes) {
	// Event-Level Parameter (item-list-id, item-list-name)
	const eventParams = {};

	const componentElement = element.closest("[component]");
	if (componentElement) {
		const componentName = componentElement.getAttribute("component");
		if (componentName) {
			if (eventName === "select_promotion") {
				eventParams["creative_slot"] = componentName;
			} else if (EVENT_LEVEL_LIST_PARAMS_EVENTS.includes(eventName)) {
				eventParams["item-list-id"] = componentName;
			}
		}

		const productList = componentElement.getAttribute("product-list");
		if (productList && EVENT_LEVEL_LIST_PARAMS_EVENTS.includes(eventName)) {
			eventParams["item-list-name"] = productList;
		}
	}

	// Weitere Event-Level Attribute (nicht dl-product spezifisch)
	Object.entries(attributes).forEach(([key, value]) => {
		// Überspringe Attribute die in Items gehören
		if (!isItemAttribute(key)) {
			eventParams[key] = value;
		}
	});

	// impression (von ancestor [impression] Element)
	const impressionElement = element.closest("[impression]");
	if (impressionElement) {
		eventParams.impression = impressionElement.getAttribute("impression");
	}

	// Sammle zusätzliche Attribute vom component-Element
	const componentAttributes = collectComponentAttributes(element);
	Object.assign(eventParams, componentAttributes);

	// Items sammeln
	const items = collectDlEventItems(element);

	applyClickContextList(eventName, element, items);

	return {
		element,
		eventName,
		params: eventParams,
		items
	};
}

/**
 * Setzt item_list_id/item_list_name aus dem Klick-Kontext auf die Items — nur für
 * Attribution-Events und nur innerhalb einer Produktliste (impression="product"). Hat
 * Vorrang vor der gespeicherten productAttribution und schreibt nichts in den localStorage.
 * @private
 * @param {string} eventName
 * @param {HTMLElement} element - Element mit dl-event Attribut
 * @param {Array<Object>} items - Items, die in-place angereichert werden
 */
function applyClickContextList(eventName, element, items) {
	if (!PRODUCT_ATTRIBUTION_EVENTS.includes(eventName)) {
		return;
	}

	const impressionElement = element.closest("[impression]");
	if (!impressionElement || impressionElement.getAttribute("impression") !== "product") {
		return;
	}

	const componentElement = element.closest("[component]");
	if (!componentElement) {
		return;
	}

	const componentName = componentElement.getAttribute("component");
	if (!componentName) {
		return;
	}

	const productList = componentElement.getAttribute("product-list");
	const listName = productList || componentName;

	for (const item of items) {
		item.item_list_id = componentName;
		item.item_list_name = listName;
	}
}

/**
 * Baut den Payload für ein Non-Ecommerce dl-event (analog zu click Event)
 * @private
 */
function buildDlNonEcommercePayload(eventName, element, attributes) {
	const params = { ...attributes };

	// component
	const componentElement = element.closest("[component]");
	if (componentElement && !params.component) {
		params.component = componentElement.getAttribute("component");
	}

	// list (von product-list)
	const productListElement = element.closest("[product-list]");
	if (productListElement && !params.list) {
		params.list = productListElement.getAttribute("product-list");
	}

	return {
		element,
		eventName,
		params
	};
}

/**
 * Sammelt Attribute vom dl-event Element
 * @private
 */
function collectDlEventAttributes(element) {
	const attributes = {};

	if (!element || !element.attributes) {
		return attributes;
	}

	for (const attr of element.attributes) {
		const name = attr.name;
		const value = attr.value;

		// dl-event selbst überspringen
		if (name === "dl-event") {
			continue;
		}

		// Leere Werte ignorieren
		if (!value) {
			continue;
		}

		// Ignorierte Attribute überspringen
		if (IGNORED_ATTRIBUTES.includes(name)) {
			continue;
		}

		attributes[name] = value;
	}

	return attributes;
}

/**
 * Sammelt Items für Ecommerce Events
 * @private
 */
function collectDlEventItems(element) {
	const items = [];

	// 1. dl-product Elemente haben Priorität
	const dlProducts = element.querySelectorAll("dl-product");
	if (dlProducts.length > 0) {
		dlProducts.forEach((product) => {
			const itemData = collectAttributes(product);
			if (Object.keys(itemData).length > 0) {
				items.push(itemData);
			}
		});
		return items;
	}

	// 2. Element mit product-id
	const productElement = element.hasAttribute("product-id")
		? element
		: element.querySelector("[product-id]");

	if (productElement) {
		const itemData = collectAttributes(productElement);
		if (Object.keys(itemData).length > 0) {
			items.push(itemData);
		}
	}

	return items;
}

/**
 * Prüft ob ein Attribut zu Items gehört (nicht zu Event-Level)
 * @private
 */
function isItemAttribute(key) {
	const itemAttributes = [
		'item-id', 'item_id', 'product-id',
		'item-name', 'item_name', 'name',
		'price', 'quantity', 'category',
		'item-category', 'item_category'
	];
	return itemAttributes.includes(key);
}

/**
 * Sammelt alle Attribute eines Elements
 * Diese Funktion wird von mehreren Trackern verwendet (dl-event, click, etc.)
 * @param {HTMLElement} element - Element zum Auslesen
 * @returns {Object} Objekt mit allen Attributen
 */
export function collectAttributes(element) {
	const attributes = {};

	if (!element || !element.attributes) {
		return attributes;
	}

	for (const attr of element.attributes) {
		if (attr.value) {
			attributes[attr.name] = attr.value;
		}
	}

	return attributes;
}
